import React, { useEffect, useRef, useState } from 'react';
import { teamMembersAPI } from '../services/websiteAPI';
import ConfirmModal from '../components/ConfirmModal';

const TeamMembers = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ show: false, id: null, name: '' });
  const [draggedMember, setDraggedMember] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [visibilitySavingIds, setVisibilitySavingIds] = useState([]);
  const formTopRef = useRef(null);
  const nameInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    image: '',
    linkedin: '',
    bio: '',
    displayOrder: 0,
    isActive: true
  });

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      const response = await teamMembersAPI.getAll({ includeInactive: true });
      setMembers(response.data);
    } catch (err) {
      console.error('Failed to load team members', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    try {
      setUploadingImage(true);
      const response = await teamMembersAPI.uploadImage(file);
      const data = response.data;
      setFormData({ ...formData, image: data.imageUrl });
    } catch (err) {
      console.error('Failed to upload image', err);
      alert('Failed to upload image: ' + (err.response?.data?.error || err.message));
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await teamMembersAPI.update(editingId, formData);
      } else {
        await teamMembersAPI.create(formData);
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', title: '', image: '', linkedin: '', bio: '', displayOrder: 0, isActive: true });
      setImagePreview(null);
      await loadMembers();
    } catch (err) {
      alert(`Failed to ${editingId ? 'update' : 'create'} team member: ` + err.response?.data?.error);
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await teamMembersAPI.getById(id);
      const member = response.data;
      setFormData({
        name: member.Name,
        title: member.Title || '',
        image: member.Image || '',
        linkedin: member.LinkedIn || '',
        bio: member.Bio || '',
        displayOrder: member.DisplayOrder || 0,
        isActive: Boolean(member.IsActive)
      });
      setImagePreview(member.Image || null);
      setEditingId(id);
      setShowForm(true);
      setTimeout(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        nameInputRef.current?.focus();
      }, 0);
    } catch (err) {
      alert('Failed to load team member for editing');
    }
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', title: '', image: '', linkedin: '', bio: '', displayOrder: 0, isActive: true });
    setImagePreview(null);
  };

  const handleDelete = async () => {
    try {
      await teamMembersAPI.delete(confirmDelete.id);
      await loadMembers();
      setConfirmDelete({ show: false, id: null, name: '' });
    } catch (err) {
      console.error('Delete error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete team member';
      alert(`Failed to delete team member: ${errorMsg}`);
      setConfirmDelete({ show: false, id: null, name: '' });
    }
  };

  const handleVisibilityChange = async (member, isActive) => {
    const id = member.TeamMemberId;
    setVisibilitySavingIds(previous => [...previous, id]);
    try {
      const response = await teamMembersAPI.updateVisibility(id, isActive);
      const savedIsActive = Boolean(response.data.IsActive);
      setMembers(previous => previous.map(item => item.TeamMemberId === id
        ? { ...item, IsActive: savedIsActive }
        : item));
      if (editingId === id) {
        setFormData(previous => ({ ...previous, isActive: savedIsActive }));
      }
    } catch (err) {
      console.error('Failed to update team member visibility', err);
      alert('Failed to update team member visibility: ' + (err.response?.data?.error || err.message));
    } finally {
      setVisibilitySavingIds(previous => previous.filter(item => item !== id));
    }
  };

  const handleDragStart = (e, member) => {
    setDraggedMember(member);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetMember) => {
    e.preventDefault();
    if (!draggedMember || draggedMember.TeamMemberId === targetMember.TeamMemberId) {
      setDraggedMember(null);
      return;
    }

    const newMembers = [...members];
    const draggedIndex = newMembers.findIndex(m => m.TeamMemberId === draggedMember.TeamMemberId);
    const targetIndex = newMembers.findIndex(m => m.TeamMemberId === targetMember.TeamMemberId);

    newMembers.splice(draggedIndex, 1);
    newMembers.splice(targetIndex, 0, draggedMember);

    const updatedMembers = newMembers.map((member, index) => ({
      ...member,
      DisplayOrder: index
    }));

    setMembers(updatedMembers);
    setDraggedMember(null);
  };

  const handleDragEnd = () => {
    setDraggedMember(null);
  };

  const handleSaveOrder = async () => {
    try {
      setSaving(true);
      const orders = members.map((member, index) => ({
        id: member.TeamMemberId,
        displayOrder: index
      }));

      await teamMembersAPI.reorder(orders);
      setReorderMode(false);
      await loadMembers();
      alert('Team member order saved successfully!');
    } catch (err) {
      console.error('Failed to save order', err);
      alert('Failed to save team member order');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelReorder = async () => {
    setReorderMode(false);
    await loadMembers();
  };

  if (loading) return <div className="loading">Loading team members...</div>;

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Team Members</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {reorderMode ? (
            <>
              <button onClick={handleSaveOrder} disabled={saving} className="btn btn-primary" style={{ backgroundColor: '#28a745' }}>
                {saving ? 'Saving...' : 'Save Order'}
              </button>
              <button onClick={handleCancelReorder} disabled={saving} className="btn btn-secondary">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setReorderMode(true)} className="btn btn-secondary">
                Reorder
              </button>
              <button onClick={() => editingId ? handleCancelEdit() : setShowForm(!showForm)} className="btn btn-primary">
                {showForm ? 'Cancel' : 'Add Team Member'}
              </button>
            </>
          )}
        </div>
      </div>

      {reorderMode && (
        <div style={{
          backgroundColor: '#f0f7ff',
          border: '2px solid #0078d4',
          borderRadius: '4px',
          padding: '12px',
          marginBottom: '20px',
          color: '#0078d4'
        }}>
          📋 Drag and drop team members to reorder them. Click "Save Order" when finished.
        </div>
      )}

      {showForm && (
        <div className="card" ref={formTopRef}>
          <h2>{editingId ? 'Edit Team Member' : 'New Team Member'}</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name *</label>
              <input
                type="text"
                placeholder="e.g., Mark Arzoomanian"
                required
                ref={nameInputRef}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Title</label>
              <input
                type="text"
                placeholder="e.g., Chairman & Chief Executive Officer"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Profile Image</label>
              <div style={{ marginBottom: '10px' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ width: '100%', padding: '8px' }}
                />
                <small style={{ color: '#666' }}>Upload a JPG, PNG, or WebP image (max 5MB)</small>
              </div>
              {uploadingImage && (
                <div style={{ color: '#0078d4', marginBottom: '10px' }}>Uploading image...</div>
              )}
              {imagePreview && (
                <div style={{ marginBottom: '10px' }}>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '200px', 
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }} 
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>LinkedIn URL</label>
              <input
                type="url"
                placeholder="https://linkedin.com/in/username"
                value={formData.linkedin}
                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Bio</label>
              <textarea
                placeholder="Team member biography"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows="6"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Display Order</label>
              <input
                type="number"
                placeholder="0"
                value={formData.displayOrder}
                onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
                style={{ width: '100%' }}
              />
              <small style={{ color: '#666' }}>Lower numbers appear first (or use Reorder button)</small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(event) => setFormData({ ...formData, isActive: event.target.checked })}
                /> Show on website
              </label>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" onClick={handleCancelEdit} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="team-grid" style={{ display: 'grid', gap: '20px', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
        {members.map((member) => (
          <div
            key={member.TeamMemberId}
            className="card"
            draggable={reorderMode}
            onDragStart={(e) => handleDragStart(e, member)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, member)}
            onDragEnd={handleDragEnd}
            style={{
              textAlign: 'center',
              cursor: reorderMode ? 'move' : 'default',
              opacity: draggedMember?.TeamMemberId === member.TeamMemberId ? 0.5 : 1,
              border: reorderMode ? '2px dashed #0078d4' : '1px solid #ddd',
              transition: 'all 0.2s ease',
              backgroundColor: reorderMode ? '#f9f9f9' : 'white'
            }}
          >
            {member.Image && (
              <img 
                src={member.Image} 
                alt={member.Name}
                style={{ 
                  width: '150px', 
                  height: '150px', 
                  borderRadius: '50%', 
                  objectFit: 'cover', 
                  margin: '0 auto 15px',
                  display: 'block',
                  pointerEvents: 'none'
                }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <h3 style={{ marginTop: 0, marginBottom: '5px' }}>{member.Name}</h3>
            {member.Title && (
              <div style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
                {member.Title}
              </div>
            )}
            {member.LinkedIn && (
              <div style={{ marginBottom: '10px' }}>
                <a href={member.LinkedIn} target="_blank" rel="noopener noreferrer" style={{ color: '#0078d4' }}>
                  LinkedIn Profile
                </a>
              </div>
            )}
            {member.Bio && (
              <div style={{ fontSize: '13px', color: '#555', marginBottom: '15px', textAlign: 'left' }}>
                {member.Bio.length > 200 
                  ? member.Bio.substring(0, 200) + '...' 
                  : member.Bio}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: 'auto' }}>
              {!reorderMode && (
                <>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(member.IsActive)}
                      disabled={visibilitySavingIds.includes(member.TeamMemberId)}
                      onChange={(event) => handleVisibilityChange(member, event.target.checked)}
                      aria-label={`Show ${member.Name} on website`}
                    /> {visibilitySavingIds.includes(member.TeamMemberId) ? 'Saving...' : 'Show on website'}
                  </label>
                  <button onClick={() => handleEdit(member.TeamMemberId)} className="btn btn-sm btn-secondary">
                    Edit
                  </button>
                  <button onClick={() => setConfirmDelete({ show: true, id: member.TeamMemberId, name: member.Name })} className="btn btn-sm btn-danger">
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
        {members.length === 0 && (
          <div className="card" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
            No team members found. Click "Add Team Member" to create one.
          </div>
        )}
      </div>

      {confirmDelete.show && (
        <ConfirmModal
          show={true}
          title="Delete Team Member"
          message={`Are you sure you want to delete ${confirmDelete.name}? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete({ show: false, id: null, name: '' })}
          confirmText="Delete"
          confirmStyle="danger"
        />
      )}
    </div>
  );
};

export default TeamMembers;
